import { useQueries, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { selectEssSwitch } from "shared/slice/essSwitchSlice";
import { selectTesSwitch } from "shared/slice/tesSwitchSlice";
import { getSSRsForDeviceService } from "shared/services";
import { ssrStateKey } from "shared/hooks/useSsrState";
import { buildSsrStateFromRest } from "shared/helpers/ssrCache";
import { useSsrElementsBank } from "./useSsrElementsBank";
import { buildSpecsFromHeaters } from "shared/helpers/buildSpecsFromHeaters";

// Pairs every machine with its own location and machine key, so the two can
// never drift apart the way a filtered array index does.
// faultedOnly: the faults page only needs machines with an active SSR fault;
// report status needs all of them, or its per-SSR report stays empty.
export const getSSRFetchTargets = (switchStatus, { faultedOnly = true } = {}) =>
  Object.entries(switchStatus).flatMap(([location, zone]) =>
    Object.entries(zone)
      .filter(([, machine]) => !faultedOnly || machine.ssrFault?.includes(1))
      .map(([machineKey, machine]) => ({ location, machineKey, machine }))
  );

export const useGetAllSSRsQueries = (switchStatus, swtName, options) => {
  const queryClient = useQueryClient();

  const { elementsOptions } = useSsrElementsBank();
  const { flatEssSwitch, flatTesSwitch } = useSelector(
    swtName === 'ESS'
      ? selectEssSwitch
      : selectTesSwitch
  );

  const swtStatus = swtName === 'ESS' ? flatEssSwitch : flatTesSwitch;

  const getSSRsQueries = useQueries(
    getSSRFetchTargets(switchStatus, options)
      .map(({ location, machineKey: machine, machine: machineData }) => ({
            queryKey: [machineData.deviceMac, '-ssrsData'],
            queryFn: () => getSSRsForDeviceService(machineData.deviceMac),
            onSuccess: (data) => {
              if (data) {
                const result = data.map((item) => {
                  const specs = buildSpecsFromHeaters(item.Heaters, elementsOptions);
                  return {
                    ...item,
                    select: `tc-${swtStatus[location]?.[machine]?.heaterThermocoupleMap?.[item.No]}`,
                    buttonStatus: item?.fault === true || item?.Load_exceeded === true ? 'flt' :
                      item?.active === true ? 'on' : 'off',
                    specs: specs.length !== 0 ? specs : [{}],
                    switchName: `${item.name} ${item.size}`,
                  };
                });

                queryClient.setQueryData(ssrStateKey(machine), (prev) =>
                  buildSsrStateFromRest(prev, result)
                );
              }
            },
            onError: (error) => {
              console.error('get ssrs fail:', error.message);
            },
          }))
  );

  return getSSRsQueries;
};